// src/services/analyticsService.js
import axiosConfig from "../config/axiosConfig";

class AnalyticsService {
  constructor() {
    this.axiosInstance = axiosConfig.getInstance();
  }

  // ==================== USER ANALYTICS ====================

  /**
   * Get analytics for all users with pagination and sorting
   * GET /api/admin/analytics/users
   */
  async getAllUsersAnalytics(page = 1, limit = 20, sortBy = 'total_time', days = 30) {
    try {
      const response = await this.axiosInstance.get('/api/admin/analytics/users', {
        params: { page, limit, sort_by: sortBy, days }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get all users analytics:', error);
      throw error;
    }
  }

  /**
   * Get detailed analytics for a specific user
   * GET /api/admin/analytics/users/{user_id}
   */
  async getUserAnalytics(userId, days = 30) {
    try {
      const response = await this.axiosInstance.get(`/api/admin/analytics/users/${userId}`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get analytics for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get platform-wide analytics overview
   * GET /api/admin/analytics/platform/overview
   */
  // async getPlatformOverview(days = 30) {
  //   try {
  //     const response = await this.axiosInstance.get('/api/admin/analytics/platform/overview', {
  //       params: { days }
  //     });
  //     return response.data;
  //   } catch (error) {
  //     console.error('Failed to get platform overview:', error);
  //     throw error;
  //   }
  // }

  /**
   * Get detailed session history for a user
   * GET /api/admin/analytics/users/{user_id}/sessions
   */
  async getUserSessions(userId, page = 1, limit = 10, days = 30) {
    try {
      const response = await this.axiosInstance.get(`/api/admin/analytics/users/${userId}/sessions`, {
        params: { page, limit, days }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get sessions for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get detailed activity history for a user
   * GET /api/admin/analytics/users/{user_id}/activities
   */
  async getUserActivities(userId, page = 1, limit = 20, activityType = null, days = 7) {
    try {
      const params = { page, limit, days };
      if (activityType) {
        params.activity_type = activityType;
      }

      const response = await this.axiosInstance.get(`/api/admin/analytics/users/${userId}/activities`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get activities for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Manually refresh daily stats for a user
   * POST /api/admin/analytics/users/{user_id}/refresh-stats
   */
  async refreshUserStats(userId, days = 7) {
    try {
      const response = await this.axiosInstance.post(
        `/api/admin/analytics/users/${userId}/refresh-stats`,
        {},
        { params: { days } }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to refresh stats for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get analytics system summary
   * GET /api/admin/analytics/stats/summary
   */
  async getAnalyticsSystemSummary() {
    try {
      const response = await this.axiosInstance.get('/api/admin/analytics/stats/summary');
      return response.data;
    } catch (error) {
      console.error('Failed to get analytics system summary:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Format duration in seconds to human readable string
   */
  formatDuration(seconds) {
    if (!seconds || seconds < 0) return '0s';
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }

  /**
   * Format file size in bytes to human readable string
   */
  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    let size = bytes;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Format timestamp to relative time (e.g., "2 hours ago")
   */
  formatRelativeTime(timestamp) {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return time.toLocaleDateString();
  }

  /**
   * Get activity type display name
   */
  getActivityTypeDisplayName(activityType) {
    const displayNames = {
      'login': 'Login',
      'video_upload': 'Video Upload',
      'job_create': 'Job Created',
      'job_complete': 'Job Completed',
      'video_delete': 'Video Deleted',
      'job_cancel': 'Job Cancelled',
      'session_start': 'Session Started',
      'session_end': 'Session Ended'
    };
    
    return displayNames[activityType] || activityType;
  }

  /**
   * Get activity type icon name (for Material-UI icons)
   */
  getActivityTypeIcon(activityType) {
    const iconNames = {
      'login': 'Login',
      'video_upload': 'VideoLibrary',
      'job_create': 'Work',
      'job_complete': 'CheckCircle',
      'video_delete': 'Delete',
      'job_cancel': 'Cancel',
      'session_start': 'PlayArrow',
      'session_end': 'Stop'
    };
    
    return iconNames[activityType] || 'Help';
  }

  /**
   * Get activity type color
   */
  getActivityTypeColor(activityType) {
    const colors = {
      'login': 'primary',
      'video_upload': 'info',
      'job_create': 'success',
      'job_complete': 'success',
      'video_delete': 'error',
      'job_cancel': 'warning',
      'session_start': 'primary',
      'session_end': 'default'
    };
    
    return colors[activityType] || 'default';
  }

  /**
   * Calculate engagement score based on user metrics
   */
  calculateEngagementScore(userMetrics) {
    if (!userMetrics) return 0;
    
    const {
      total_time_seconds = 0,
      total_sessions = 0,
      total_videos = 0,
      total_jobs = 0,
      avg_session_duration = 0
    } = userMetrics;
    
    // Weighted scoring system
    const timeScore = Math.min(total_time_seconds / 3600, 10) * 2; // Max 20 points for time
    const sessionScore = Math.min(total_sessions, 20) * 1; // Max 20 points for sessions
    const contentScore = Math.min((total_videos + total_jobs) * 2, 30); // Max 30 points for content
    const qualityScore = Math.min(avg_session_duration / 60, 30); // Max 30 points for session quality
    
    const totalScore = timeScore + sessionScore + contentScore + qualityScore;
    return Math.round(totalScore);
  }

  /**
   * Get engagement level based on score
   */
  getEngagementLevel(score) {
    if (score >= 80) return { level: 'Very High', color: 'success' };
    if (score >= 60) return { level: 'High', color: 'info' };
    if (score >= 40) return { level: 'Medium', color: 'warning' };
    if (score >= 20) return { level: 'Low', color: 'error' };
    return { level: 'Very Low', color: 'error' };
  }

  /**
   * Export analytics data to CSV format
   */
  exportToCSV(data, filename = 'analytics_export.csv') {
    try {
      let csvContent = '';
      
      if (Array.isArray(data) && data.length > 0) {
        // Get headers from first object
        const headers = Object.keys(data[0]);
        csvContent += headers.join(',') + '\n';
        
        // Add data rows
        data.forEach(row => {
          const values = headers.map(header => {
            let value = row[header];
            
            // Handle nested objects or arrays
            if (typeof value === 'object' && value !== null) {
              value = JSON.stringify(value);
            }
            
            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === 'string') {
              value = value.replace(/"/g, '""');
              if (value.includes(',') || value.includes('\n') || value.includes('"')) {
                value = `"${value}"`;
              }
            }
            
            return value || '';
          });
          
          csvContent += values.join(',') + '\n';
        });
      }
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Failed to export CSV:', error);
      throw error;
    }
  }

  /**
   * Generate analytics report summary
   */
  generateReportSummary(analyticsData) {
    if (!analyticsData) return null;
    
    const summary = {
      generatedAt: new Date().toISOString(),
      totalUsers: 0,
      totalActivities: 0,
      averageEngagement: 0,
      topActivities: [],
      insights: []
    };
    
    if (analyticsData.users) {
      summary.totalUsers = analyticsData.users.length;
      
      // Calculate average engagement
      const engagementScores = analyticsData.users.map(user => 
        this.calculateEngagementScore(user.metrics)
      );
      summary.averageEngagement = engagementScores.length > 0 
        ? Math.round(engagementScores.reduce((a, b) => a + b, 0) / engagementScores.length)
        : 0;
      
      // Generate insights
      const highEngagementUsers = engagementScores.filter(score => score >= 60).length;
      const engagementPercentage = Math.round((highEngagementUsers / summary.totalUsers) * 100);
      
      summary.insights.push(`${engagementPercentage}% of users show high engagement`);
      
      if (summary.averageEngagement > 50) {
        summary.insights.push('Overall user engagement is healthy');
      } else {
        summary.insights.push('Consider strategies to improve user engagement');
      }
    }
    
    return summary;
  }

  /**
   * Handle API errors with user-friendly messages
   */
  handleApiError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || 'Unknown error';
      
      switch (status) {
        case 401:
          return 'Authentication required. Please log in again.';
        case 403:
          return 'Access denied. Admin privileges required.';
        case 404:
          return 'Requested data not found.';
        case 429:
          return 'Too many requests. Please try again later.';
        case 500:
          return 'Server error. Please try again later.';
        default:
          return message;
      }
    } else if (error.request) {
      // Network error
      return 'Network error. Please check your connection.';
    } else {
      return error.message || 'An unexpected error occurred.';
    }
  }
}

// Create and export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;